import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { JSONEditorFormItem } from '@/components/ui/form-fields/JsonEditorFormItem';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { Separator } from '@/components/ui/Separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { Textarea } from '@/components/ui/Textarea';
import {
  createDefaultCustomerSecretDrafts,
  CustomerSecretDrafts,
  CustomerSecretTarget,
  customerSecretTargets,
  getConfigValueAtPath,
  getCustomerSecretDraft,
  getCustomerSecretStatusText,
} from '@/features/Customers/customerSecretStore';
import { VlmProfileSettingsForm } from '@/features/Customers/vlmGatewayProfileSettings';
import { isSenhubGraphqlConfigured } from '@/graphql/executeSenhub';
import { cn } from '@/lib/utils';
import { CustomerConfig } from '@/models/customer.models';
import { JSONConfig } from '@/models/device.models';
import { Pencil } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import {
  Control,
  Controller,
  FieldPath,
  UseFormRegister,
  UseFormSetError,
  useWatch,
} from 'react-hook-form';
import { VlmGatewayCard, VlmSettingsReveal } from './VlmGatewayCard';

export type CustomerFormValues = {
  name: string;
  storagePath: string;
  config: CustomerConfig;
  jsonConfig: JSONConfig;
  // VLM gateway profile settings by profile name, kept apart from `config`.
  // Holds stored profiles and ones added in this edit.
  vlmProfiles?: Record<string, VlmProfileSettingsForm>;
  // Ids of the profiles added in this edit, by name.
  vlmAddedProfileIds?: Record<string, string>;
  // Stored profiles to remove on save.
  vlmRemovedProfiles?: string[];
  // New names for stored profiles, by stored name.
  vlmRenamedProfiles?: Record<string, string>;
};

export type CustomerFormMode = 'form' | 'json';

export type SecretFieldOptions = {
  disabled?: boolean;
};

export type CustomerDetailsFormProps<_TFormValues extends CustomerFormValues> =
  {
    register: UseFormRegister<CustomerFormValues>;
    control: Control<CustomerFormValues>;
    setError: UseFormSetError<CustomerFormValues>;
    readOnly?: boolean;
    className?: string;
    creatingCustomer?: boolean;
    formMode: CustomerFormMode;
    setFormMode: (mode: CustomerFormMode) => void;
    secretDrafts?: CustomerSecretDrafts;
    onStageSecretUpsert?: (
      targetId: string,
      secret: { username: string; password: string } | { apiKey: string },
    ) => void;
    onStageSecretDelete?: (targetId: string) => void;
    onDiscardSecretDraft?: (targetId: string) => void;
    // The customer's config as stored; the VLM Gateway card reads its profiles.
    storedConfig?: unknown;
    // A new value switches to the VLM Gateway tab and opens these profiles' settings.
    vlmSettingsReveal?: VlmSettingsReveal;
  };

const fixedSecretFieldPaths = customerSecretTargets.map(
  (target) =>
    `config.${target.configPath.join('.')}` as FieldPath<CustomerFormValues>,
);

const SECRET_EDITING_UNAVAILABLE_NOTE =
  'Secret editing is not available: the senhub GraphQL URL is not configured.';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const tryJsonParseString = (value: unknown): string => {
  if (typeof value !== 'string') return '';
  try {
    const parsed = JSON.parse(value);
    return typeof parsed === 'string' ? parsed : value;
  } catch {
    return value;
  }
};

const normalizeExternalServiceConfig = (
  value: unknown,
): { Url: string; ApiUsernamePasswordSecret: string } => {
  if (!isRecord(value)) {
    return {
      Url: '',
      ApiUsernamePasswordSecret: '',
    };
  }

  return {
    Url: typeof value.Url === 'string' ? value.Url : '',
    ApiUsernamePasswordSecret:
      typeof value.ApiUsernamePasswordSecret === 'string'
        ? value.ApiUsernamePasswordSecret
        : '',
  };
};

const normalizeFtpConfig = (
  value: unknown,
): { Server: string; Port: string } => {
  if (!isRecord(value)) {
    return {
      Server: '',
      Port: '',
    };
  }

  return {
    Server: typeof value.Server === 'string' ? value.Server : '',
    Port: typeof value.Port === 'string' ? value.Port : '',
  };
};

// Top-level keys normalizeCustomerConfig folds into Senpic. A form save drops
// them, so a value cleared in the form can't come back through the fallback.
export const LEGACY_SENPIC_CONFIG_KEYS = ['Senbos', 'StagingSenbos'] as const;

export const normalizeCustomerConfig = (config: unknown): CustomerConfig => {
  if (!isRecord(config)) {
    return {};
  }

  const senpicRecord = isRecord(config.Senpic) ? config.Senpic : {};
  const legacySenbos = normalizeExternalServiceConfig(config.Senbos);
  const legacyStagingSenbos = normalizeExternalServiceConfig(
    config.StagingSenbos,
  );

  const senpicSenbos = normalizeExternalServiceConfig(senpicRecord.Senbos);
  const senpicStagingSenbos = normalizeExternalServiceConfig(
    senpicRecord.StagingSenbos,
  );

  const normalizeHasura = (v: unknown) => {
    const r = isRecord(v) ? v : {};
    return {
      Url: typeof r.Url === 'string' ? r.Url : '',
      ApiKeyId: typeof r.ApiKeyId === 'number' ? r.ApiKeyId : 0,
    };
  };

  const normalizeLlm = (v: unknown) => {
    const r = isRecord(v) ? v : {};
    return {
      Model: typeof r.Model === 'string' ? r.Model : '',
      ApiKey: typeof r.ApiKey === 'string' ? r.ApiKey : '',
      ApiUrl: typeof r.ApiUrl === 'string' ? r.ApiUrl : '',
      MaxTokens: typeof r.MaxTokens === 'number' ? r.MaxTokens : 2048,
      SystemPrompt: tryJsonParseString(r.SystemPrompt),
    };
  };

  return {
    Ftp: normalizeFtpConfig(config.Ftp),

    Grafana: {
      DashboardUid:
        isRecord(config.Grafana) &&
        typeof config.Grafana.DashboardUid === 'string'
          ? config.Grafana.DashboardUid
          : '',
      DashboardSlug:
        isRecord(config.Grafana) &&
        typeof config.Grafana.DashboardSlug === 'string'
          ? config.Grafana.DashboardSlug
          : '',
      OrganizationId:
        isRecord(config.Grafana) &&
        typeof config.Grafana.OrganizationId === 'string'
          ? config.Grafana.OrganizationId
          : '',
    },
    Senpic: {
      Senbos: {
        Url: senpicSenbos.Url || legacySenbos.Url,
        ApiUsernamePasswordSecret:
          senpicSenbos.ApiUsernamePasswordSecret ||
          legacySenbos.ApiUsernamePasswordSecret,
      },
      StagingSenbos: {
        Url: senpicStagingSenbos.Url || legacyStagingSenbos.Url,
        ApiUsernamePasswordSecret:
          senpicStagingSenbos.ApiUsernamePasswordSecret ||
          legacyStagingSenbos.ApiUsernamePasswordSecret,
      },
    },
    Senshield: {
      Senbos: normalizeExternalServiceConfig(
        isRecord(config.Senshield) ? config.Senshield.Senbos : undefined,
      ),
    },
    Senforce: {
      Senbos: normalizeExternalServiceConfig(
        isRecord(config.Senforce) ? config.Senforce.Senbos : undefined,
      ),
    },
    AlertReviewer: {
      Provider:
        isRecord(config.AlertReviewer) &&
        typeof config.AlertReviewer.Provider === 'string'
          ? config.AlertReviewer.Provider
          : '',
      Senbos: normalizeExternalServiceConfig(
        isRecord(config.AlertReviewer)
          ? config.AlertReviewer.Senbos
          : undefined,
      ),
      Gpt: normalizeLlm(
        isRecord(config.AlertReviewer) ? config.AlertReviewer.Gpt : undefined,
      ),
      Claude: normalizeLlm(
        isRecord(config.AlertReviewer)
          ? config.AlertReviewer.Claude
          : undefined,
      ),
      Gemini: normalizeLlm(
        isRecord(config.AlertReviewer)
          ? config.AlertReviewer.Gemini
          : undefined,
      ),
      Hasura: normalizeHasura(
        isRecord(config.AlertReviewer)
          ? config.AlertReviewer.Hasura
          : undefined,
      ),
      FrameTypes:
        isRecord(config.AlertReviewer) &&
        Array.isArray(config.AlertReviewer.FrameTypes) &&
        (config.AlertReviewer.FrameTypes as string[]).length > 0
          ? (config.AlertReviewer.FrameTypes as string[])
          : undefined,
    },
  };
};

const FrameTypesInput = ({
  value,
  readOnly,
  onChange,
}: {
  value: string[] | undefined;
  readOnly: boolean;
  onChange: (value: string[] | undefined) => void;
}) => {
  const [text, setText] = useState(value?.join(', ') ?? '');
  return (
    <Input
      type="text"
      id="alert-reviewer-frame-types"
      readOnly={readOnly}
      placeholder="Leave empty to send all frame types"
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => {
        const val = text.trim();
        onChange(
          val
            ? val
                .split(',')
                .map((s) => s.trim().toUpperCase())
                .filter(Boolean)
            : undefined,
        );
      }}
    />
  );
};

const CustomerDetailsForm = <_TFormValues extends CustomerFormValues>({
  register,
  control,
  setError,
  readOnly = false,
  creatingCustomer = false,
  className,
  formMode,
  setFormMode,
  secretDrafts = createDefaultCustomerSecretDrafts(),
  onStageSecretUpsert,
  onStageSecretDelete,
  onDiscardSecretDraft,
  storedConfig,
  vlmSettingsReveal,
}: CustomerDetailsFormProps<_TFormValues>) => {
  const [productTab, setProductTab] = useState('senpic');
  useEffect(() => {
    if (vlmSettingsReveal) {
      setProductTab('vlm-gateway');
    }
  }, [vlmSettingsReveal]);
  const [secretEditorTarget, setSecretEditorTarget] =
    useState<CustomerSecretTarget | null>(null);
  const [secretEditorUsername, setSecretEditorUsername] = useState('');
  const [secretEditorPassword, setSecretEditorPassword] = useState('');
  const [secretEditorError, setSecretEditorError] = useState('');
  const fixedSecretValues = useWatch({ control, name: fixedSecretFieldPaths });
  const secretEditingAvailable = isSenhubGraphqlConfigured();

  const handleConfigViewChange = (value: string) => {
    const nextView = value === 'json' ? 'json' : 'form';
    setFormMode?.(nextView);
  };

  const activeSecretDraft = useMemo(() => {
    if (!secretEditorTarget) {
      return null;
    }

    return getCustomerSecretDraft(secretDrafts, secretEditorTarget.id);
  }, [secretDrafts, secretEditorTarget]);

  const openSecretEditor = (target: CustomerSecretTarget) => {
    setSecretEditorTarget(target);
    setSecretEditorError('');

    const draft = getCustomerSecretDraft(secretDrafts, target.id);
    if (draft.action === 'upsert') {
      setSecretEditorUsername(draft.username);
      setSecretEditorPassword(draft.password);
      return;
    }

    setSecretEditorUsername('');
    setSecretEditorPassword('');
  };

  const closeSecretEditor = () => {
    setSecretEditorTarget(null);
    setSecretEditorUsername('');
    setSecretEditorPassword('');
    setSecretEditorError('');
  };

  const handleSecretSave = () => {
    if (!secretEditorTarget || !onStageSecretUpsert) {
      closeSecretEditor();
      return;
    }

    const isApiKey = secretEditorTarget.secretKind === 'apiKey';

    if (isApiKey) {
      const apiKey = secretEditorPassword.trim();
      if (!apiKey) {
        setSecretEditorError('API Key is required.');
        return;
      }
      onStageSecretUpsert(secretEditorTarget.id, { apiKey });
    } else {
      const username = secretEditorUsername.trim();
      const password = secretEditorPassword.trim();

      if (!username) {
        setSecretEditorError('Username is required.');
        return;
      }

      if (!password) {
        setSecretEditorError('Password is required.');
        return;
      }

      onStageSecretUpsert(secretEditorTarget.id, { username, password });
    }

    closeSecretEditor();
  };

  const handleSecretDelete = () => {
    if (!secretEditorTarget || !onStageSecretDelete) {
      closeSecretEditor();
      return;
    }

    onStageSecretDelete(secretEditorTarget.id);
    closeSecretEditor();
  };

  // Fixed targets read the form's normalized values. Profile targets read the
  // stored config, which the form doesn't model.
  const getExistingSecretValue = (target: CustomerSecretTarget): string => {
    const fixedIndex = customerSecretTargets.findIndex(
      (t) => t.id === target.id,
    );
    if (fixedIndex === -1) {
      return getConfigValueAtPath(storedConfig, target.configPath);
    }

    const value: unknown = fixedSecretValues[fixedIndex];
    return typeof value === 'string' ? value : '';
  };

  const renderSecretField = (
    target: CustomerSecretTarget,
    inputId: string,
    label: string,
    { disabled = false }: SecretFieldOptions = {},
  ) => {
    const draft = getCustomerSecretDraft(secretDrafts, target.id);
    const statusText = getCustomerSecretStatusText(draft);
    const currentValue = getExistingSecretValue(target);
    const displayValue = draft.action === 'none' ? currentValue : statusText;
    const isStatusValue = draft.action !== 'none';

    return (
      <div className="grid gap-2">
        <Label htmlFor={inputId}>{label}</Label>
        <div className="flex gap-2">
          <Input
            type="text"
            id={inputId}
            readOnly
            disabled={disabled}
            value={displayValue}
            className={isStatusValue ? 'text-muted-foreground' : undefined}
          />
          {!readOnly && !disabled && secretEditingAvailable && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => openSecretEditor(target)}
              aria-label={`Edit ${label}`}
              title={`Edit ${label}`}
            >
              <Pencil />
            </Button>
          )}
        </div>
        {!readOnly && !secretEditingAvailable && (
          <p className="text-muted-foreground text-sm">
            {SECRET_EDITING_UNAVAILABLE_NOTE}
          </p>
        )}
      </div>
    );
  };

  const getSecretTarget = (targetId: string): CustomerSecretTarget =>
    customerSecretTargets.find((t) => t.id === targetId)!;

  const getHasExistingSecret = (target: CustomerSecretTarget): boolean =>
    getExistingSecretValue(target).trim().length > 0;

  const canDeleteSecret = Boolean(
    secretEditorTarget &&
      (getHasExistingSecret(secretEditorTarget) ||
        activeSecretDraft?.action === 'upsert' ||
        activeSecretDraft?.action === 'delete'),
  );

  return (
    <div className={cn('grid gap-6', className)}>
      <div className="grid gap-2">
        <Label htmlFor="name">Name *</Label>
        <Input
          type="text"
          id="name"
          readOnly={readOnly}
          {...register('name', {
            required: 'Name is required',
          })}
        />
      </div>
      {!creatingCustomer && (
        <div className="grid gap-2">
          <Label htmlFor="storagePath">Storage Path *</Label>
          <Input
            type="text"
            id="storagePath"
            readOnly={readOnly}
            {...register('storagePath', {
              required: 'Storage Path is required',
            })}
          />
        </div>
      )}
      <Tabs
        value={formMode}
        onValueChange={handleConfigViewChange}
        className="grid rounded-xl"
      >
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger disabled={!creatingCustomer && !readOnly} value="form">
            Form
          </TabsTrigger>
          <TabsTrigger value="json" disabled={!creatingCustomer && !readOnly}>
            JSON Form
          </TabsTrigger>
        </TabsList>
        <Card>
          <CardContent className="grid gap-4">
            <TabsContent value="form" className="grid gap-4">
              <div className="grid gap-4 sm:grid-cols-2 sm:gap-6">
                <div className="grid gap-2">
                  <Label htmlFor="ftp-port">FTP Port</Label>
                  <Input
                    type="number"
                    id="ftp-port"
                    readOnly={readOnly}
                    {...register('config.Ftp.Port')}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="ftp-server">FTP Server</Label>
                  <Input
                    type="text"
                    id="ftp-server"
                    readOnly={readOnly}
                    {...register('config.Ftp.Server')}
                  />
                </div>
              </div>

              <Separator orientation="horizontal" />

              <div className="grid gap-4 md:grid-cols-3">
                <div className="grid gap-2">
                  <Label htmlFor="grafana-dashboard-uid"> Grafana UID</Label>
                  <Input
                    type="text"
                    id="grafana-dashboard-uid"
                    readOnly={readOnly}
                    {...register('config.Grafana.DashboardUid')}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="grafana-dashboard-slug">
                    Grafana Dashboard Slug
                  </Label>
                  <Input
                    type="text"
                    id="grafana-dashboard-slug"
                    readOnly={readOnly}
                    {...register('config.Grafana.DashboardSlug')}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="grafana-organization-id">
                    Grafana Organization ID
                  </Label>
                  <Input
                    type="text"
                    id="grafana-organization-id"
                    readOnly={readOnly}
                    {...register('config.Grafana.OrganizationId')}
                  />
                </div>
              </div>

              <Separator orientation="horizontal" />

              <Tabs
                value={productTab}
                onValueChange={setProductTab}
                className="grid rounded-xl"
              >
                <TabsList className="grid w-full grid-cols-5">
                  <TabsTrigger value="senpic">Senpic</TabsTrigger>
                  <TabsTrigger value="senshield">Senshield</TabsTrigger>
                  <TabsTrigger value="senforce">Senforce</TabsTrigger>
                  <TabsTrigger value="alert-reviewer">
                    Alert Reviewer
                  </TabsTrigger>
                  <TabsTrigger value="vlm-gateway">VLM Gateway</TabsTrigger>
                </TabsList>
                <Card>
                  <CardContent>
                    <TabsContent value="senpic">
                      <div className="flex flex-col gap-4 pt-2">
                        <div className="grid gap-4 rounded-lg border p-4">
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary">Senbos</Badge>
                            <Separator className="flex-1" />
                          </div>
                          <div className="grid gap-2">
                            <Label htmlFor="senbos-url">URL</Label>
                            <Input
                              type="text"
                              id="senbos-url"
                              readOnly={readOnly}
                              {...register('config.Senpic.Senbos.Url')}
                            />
                          </div>
                          {renderSecretField(
                            getSecretTarget('senpicSenbos'),
                            'api-username-password-secret',
                            'Api Secret',
                          )}
                        </div>
                        <div className="grid gap-4 rounded-lg border p-4">
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary">Staging Senbos</Badge>
                            <Separator className="flex-1" />
                          </div>
                          <div className="grid gap-2">
                            <Label htmlFor="staging-senbos-url">URL</Label>
                            <Input
                              type="text"
                              id="staging-senbos-url"
                              readOnly={readOnly}
                              {...register('config.Senpic.StagingSenbos.Url')}
                            />
                          </div>
                          {renderSecretField(
                            getSecretTarget('senpicStagingSenbos'),
                            'staging-senbos-api-username-password-secret',
                            'Api Secret',
                          )}
                        </div>
                      </div>
                    </TabsContent>
                    <TabsContent value="senshield">
                      <div className="flex flex-col gap-4 pt-2">
                        <div className="grid gap-4 rounded-lg border p-4">
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary">Senbos</Badge>
                            <Separator className="flex-1" />
                          </div>
                          <div className="grid gap-2">
                            <Label htmlFor="senshield-senbos-url">URL</Label>
                            <Input
                              type="text"
                              id="senshield-senbos-url"
                              readOnly={readOnly}
                              {...register('config.Senshield.Senbos.Url')}
                            />
                          </div>
                          {renderSecretField(
                            getSecretTarget('senshieldSenbos'),
                            'senshield-api-username-password-secret',
                            'Api Secret',
                          )}
                        </div>
                      </div>
                    </TabsContent>
                    <TabsContent value="senforce">
                      <div className="flex flex-col gap-4 pt-2">
                        <div className="grid gap-4 rounded-lg border p-4">
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary">Senbos</Badge>
                            <Separator className="flex-1" />
                          </div>
                          <div className="grid gap-2">
                            <Label htmlFor="senforce-senbos-url">URL</Label>
                            <Input
                              type="text"
                              id="senforce-senbos-url"
                              readOnly={readOnly}
                              {...register('config.Senforce.Senbos.Url')}
                            />
                          </div>
                          {renderSecretField(
                            getSecretTarget('senforceSenbos'),
                            'senforce-api-username-password-secret',
                            'Api Secret',
                          )}
                        </div>
                      </div>
                    </TabsContent>
                    <TabsContent value="alert-reviewer">
                      <div className="flex flex-col gap-5 pt-2">
                        <div className="grid gap-2">
                          <Label htmlFor="alert-reviewer-provider">
                            Active Provider
                          </Label>
                          <Controller
                            name="config.AlertReviewer.Provider"
                            control={control}
                            render={({ field }) => (
                              <Select
                                value={field.value}
                                onValueChange={field.onChange}
                                disabled={readOnly}
                              >
                                <SelectTrigger
                                  id="alert-reviewer-provider"
                                  className="w-full"
                                >
                                  <SelectValue placeholder="Select provider" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="Claude">Claude</SelectItem>
                                  <SelectItem value="Gpt">Gpt</SelectItem>
                                  <SelectItem value="Gemini">Gemini</SelectItem>
                                </SelectContent>
                              </Select>
                            )}
                          />
                        </div>

                        <div className="grid gap-2">
                          <Label htmlFor="alert-reviewer-frame-types">
                            Frame Types
                          </Label>
                          <Controller
                            name="config.AlertReviewer.FrameTypes"
                            control={control}
                            render={({ field }) => (
                              <FrameTypesInput
                                value={field.value}
                                readOnly={readOnly}
                                onChange={field.onChange}
                              />
                            )}
                          />
                        </div>

                        <div className="border-green/40 bg-green/5 grid gap-4 rounded-lg border p-4">
                          <div className="flex items-center gap-2">
                            <Badge variant="green">GPT</Badge>
                            <Separator className="flex-1" />
                          </div>
                          <div className="grid gap-4 md:grid-cols-3">
                            <div className="grid gap-2">
                              <Label>Model</Label>
                              <Input
                                type="text"
                                readOnly={readOnly}
                                {...register('config.AlertReviewer.Gpt.Model')}
                              />
                            </div>
                            <div className="grid gap-2">
                              <Label>API URL</Label>
                              <Input
                                type="text"
                                readOnly={readOnly}
                                {...register('config.AlertReviewer.Gpt.ApiUrl')}
                              />
                            </div>
                            <div className="grid gap-2">
                              <Label>Max Tokens</Label>
                              <Input
                                type="number"
                                readOnly={readOnly}
                                {...register(
                                  'config.AlertReviewer.Gpt.MaxTokens',
                                  { valueAsNumber: true },
                                )}
                              />
                            </div>
                          </div>
                          {renderSecretField(
                            getSecretTarget('alertReviewerGptApiKey'),
                            'alert-reviewer-gpt-api-key',
                            'API Key',
                          )}
                          <div className="grid gap-2">
                            <Label>System Prompt</Label>
                            <Textarea
                              readOnly={readOnly}
                              rows={4}
                              className="font-mono text-xs"
                              {...register(
                                'config.AlertReviewer.Gpt.SystemPrompt',
                              )}
                            />
                          </div>
                        </div>

                        <div className="border-orange/40 bg-orange/5 grid gap-4 rounded-lg border p-4">
                          <div className="flex items-center gap-2">
                            <Badge variant="orange">Claude</Badge>
                            <Separator className="flex-1" />
                          </div>
                          <div className="grid gap-4 md:grid-cols-3">
                            <div className="grid gap-2">
                              <Label>Model</Label>
                              <Input
                                type="text"
                                readOnly={readOnly}
                                {...register(
                                  'config.AlertReviewer.Claude.Model',
                                )}
                              />
                            </div>
                            <div className="grid gap-2">
                              <Label>API URL</Label>
                              <Input
                                type="text"
                                readOnly={readOnly}
                                {...register(
                                  'config.AlertReviewer.Claude.ApiUrl',
                                )}
                              />
                            </div>
                            <div className="grid gap-2">
                              <Label>Max Tokens</Label>
                              <Input
                                type="number"
                                readOnly={readOnly}
                                {...register(
                                  'config.AlertReviewer.Claude.MaxTokens',
                                  { valueAsNumber: true },
                                )}
                              />
                            </div>
                          </div>
                          {renderSecretField(
                            getSecretTarget('alertReviewerClaudeApiKey'),
                            'alert-reviewer-claude-api-key',
                            'API Key',
                          )}
                          <div className="grid gap-2">
                            <Label>System Prompt</Label>
                            <Textarea
                              readOnly={readOnly}
                              rows={4}
                              className="font-mono text-xs"
                              {...register(
                                'config.AlertReviewer.Claude.SystemPrompt',
                              )}
                            />
                          </div>
                        </div>

                        <div className="border-blue/40 bg-blue/5 grid gap-4 rounded-lg border p-4">
                          <div className="flex items-center gap-2">
                            <Badge variant="blue">Gemini</Badge>
                            <Separator className="flex-1" />
                          </div>
                          <div className="grid gap-4 md:grid-cols-3">
                            <div className="grid gap-2">
                              <Label>Model</Label>
                              <Input
                                type="text"
                                readOnly={readOnly}
                                {...register(
                                  'config.AlertReviewer.Gemini.Model',
                                )}
                              />
                            </div>
                            <div className="grid gap-2">
                              <Label>API URL</Label>
                              <Input
                                type="text"
                                readOnly={readOnly}
                                {...register(
                                  'config.AlertReviewer.Gemini.ApiUrl',
                                )}
                              />
                            </div>
                            <div className="grid gap-2">
                              <Label>Max Tokens</Label>
                              <Input
                                type="number"
                                readOnly={readOnly}
                                {...register(
                                  'config.AlertReviewer.Gemini.MaxTokens',
                                  { valueAsNumber: true },
                                )}
                              />
                            </div>
                          </div>
                          {renderSecretField(
                            getSecretTarget('alertReviewerGeminiApiKey'),
                            'alert-reviewer-gemini-api-key',
                            'API Key',
                          )}
                          <div className="grid gap-2">
                            <Label>System Prompt</Label>
                            <Textarea
                              readOnly={readOnly}
                              rows={4}
                              className="font-mono text-xs"
                              {...register(
                                'config.AlertReviewer.Gemini.SystemPrompt',
                              )}
                            />
                          </div>
                        </div>

                        <div className="grid gap-4 rounded-lg border p-4">
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary">Hasura</Badge>
                            <Separator className="flex-1" />
                          </div>
                          <div className="grid gap-4 sm:grid-cols-2">
                            <div className="grid gap-2">
                              <Label htmlFor="alert-reviewer-hasura-url">
                                URL
                              </Label>
                              <Input
                                type="text"
                                id="alert-reviewer-hasura-url"
                                readOnly={readOnly}
                                {...register('config.AlertReviewer.Hasura.Url')}
                              />
                            </div>
                            <div className="grid gap-2">
                              <Label htmlFor="alert-reviewer-hasura-apikeyid">
                                API Key ID
                              </Label>
                              <Input
                                type="number"
                                id="alert-reviewer-hasura-apikeyid"
                                readOnly={readOnly}
                                {...register(
                                  'config.AlertReviewer.Hasura.ApiKeyId',
                                  { valueAsNumber: true },
                                )}
                              />
                            </div>
                          </div>
                        </div>

                        <div className="grid gap-4 rounded-lg border p-4">
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary">Senbos</Badge>
                            <Separator className="flex-1" />
                          </div>
                          <div className="grid gap-2">
                            <Label htmlFor="alert-reviewer-senbos-url">
                              URL
                            </Label>
                            <Input
                              type="text"
                              id="alert-reviewer-senbos-url"
                              readOnly={readOnly}
                              {...register('config.AlertReviewer.Senbos.Url')}
                            />
                          </div>
                          {renderSecretField(
                            getSecretTarget('alertReviewerSenbos'),
                            'alert-reviewer-api-username-password-secret',
                            'Api Secret',
                          )}
                        </div>
                      </div>
                    </TabsContent>
                    <TabsContent value="vlm-gateway">
                      <VlmGatewayCard
                        storedConfig={storedConfig}
                        renderSecretField={renderSecretField}
                        control={control}
                        readOnly={readOnly}
                        secretDrafts={secretDrafts}
                        secretEditingAvailable={secretEditingAvailable}
                        onDiscardSecretDraft={onDiscardSecretDraft}
                        settingsReveal={vlmSettingsReveal}
                      />
                    </TabsContent>
                  </CardContent>
                </Card>
              </Tabs>
            </TabsContent>
            <TabsContent value="json">
              <Controller
                name="jsonConfig"
                control={control}
                render={({ field }) => (
                  <div className="flex flex-col gap-2">
                    <p className="text-muted-foreground text-sm">
                      JSON mode is advanced and full-control. Secret reference
                      fields can be edited directly here.
                    </p>
                    <div className="h-[60vh] min-h-[400px]">
                      <JSONEditorFormItem
                        onValueChange={field.onChange}
                        value={field.value}
                        onSetError={() => {
                          setError('jsonConfig', {
                            message: 'Invalid JSON!',
                          });
                        }}
                        mode={
                          creatingCustomer ? 'text' : readOnly ? 'view' : 'text'
                        }
                        options={{
                          navigationBar: true,
                          mainMenuBar: true,
                        }}
                      />
                    </div>
                  </div>
                )}
              />
            </TabsContent>
          </CardContent>
        </Card>
      </Tabs>
      <Dialog open={!!secretEditorTarget} onOpenChange={closeSecretEditor}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {secretEditorTarget
                ? `Edit ${secretEditorTarget.label}`
                : 'Edit Secret'}
            </DialogTitle>
            <DialogDescription>
              Stage username/password changes or mark this secret for deletion.
              Changes are applied when you save the customer in Form mode.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4">
            {secretEditorTarget?.secretKind !== 'apiKey' && (
              <div className="grid gap-2">
                <Label htmlFor="secret-username">Username</Label>
                <Input
                  id="secret-username"
                  type="text"
                  value={secretEditorUsername}
                  onChange={(event) =>
                    setSecretEditorUsername(event.target.value)
                  }
                />
              </div>
            )}
            <div className="grid gap-2">
              <Label htmlFor="secret-password">
                {secretEditorTarget?.secretKind === 'apiKey'
                  ? 'API Key'
                  : 'Password'}
              </Label>
              <Input
                id="secret-password"
                type="password"
                value={secretEditorPassword}
                onChange={(event) =>
                  setSecretEditorPassword(event.target.value)
                }
              />
            </div>
            {secretEditorError && (
              <p className="text-destructive text-sm">{secretEditorError}</p>
            )}
            {activeSecretDraft?.action === 'delete' && (
              <p className="text-muted-foreground text-sm">
                Secret deletion is currently staged.
              </p>
            )}
          </div>
          <DialogFooter className="flex items-center justify-between sm:justify-between">
            <Button
              type="button"
              variant="destructive"
              onClick={handleSecretDelete}
              disabled={!canDeleteSecret}
            >
              Delete Secret
            </Button>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={closeSecretEditor}
              >
                Cancel
              </Button>
              <Button type="button" onClick={handleSecretSave}>
                OK
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerDetailsForm;
